/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { GUI_LANGUAGES } from '@/i18n';

// Single source of truth: the canonical GUI language list (see src/i18n.ts).
const languages = GUI_LANGUAGES;

interface LanguageSelectorProps {
  className?: string;
  compact?: boolean;
  /** Tiny icon button matching the floating header pins (.pin-floating in index.css). */
  pin?: boolean;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  compact = false,
  pin = false
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = async (languageCode: string) => {
    try {
      await i18n.changeLanguage(languageCode);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  // Shared by the compact and pin triggers — both drop the same short list below the button.
  const shortDropdown = isOpen && (
    <>
      <div
        className="fixed inset-0 z-10"
        onClick={() => setIsOpen(false)}
      />
      <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-[9999] max-h-[calc(100vh-5rem)] overflow-y-auto">
        {languages.map((language) => (
          <button
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center space-x-2 ${
              language.code === i18n.language
                ? 'bg-primary-50 text-primary-700 border-l-2 border-primary-500'
                : 'text-gray-700'
            }`}
          >
            <span>{language.flag}</span>
            <span className="font-medium">{language.nativeName}</span>
            <span className="text-gray-500 text-xs ml-auto">{language.name}</span>
          </button>
        ))}
      </div>
    </>
  );

  if (pin) {
    return (
      <div className={`relative ${className}`}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="pin-floating pin-action pin-action-lang"
          title={currentLanguage.nativeName}
          aria-label={currentLanguage.nativeName}
        >
          <span className="pin-lang-flag">{currentLanguage.flag}</span>
          <span className="pin-lang-name">{currentLanguage.nativeName}</span>
        </button>
        {shortDropdown}
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 bg-white rounded-md border border-gray-200 hover:border-gray-300 transition-colors"
        >
          <span className="text-sm">{currentLanguage.flag}</span>
          <span className="hidden sm:inline text-xs">{currentLanguage.code.toUpperCase()}</span>
          <ChevronDown className="h-3 w-3" />
        </button>

        {shortDropdown}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
      >
        <Globe className="h-4 w-4" />
        <span className="font-medium">{currentLanguage.nativeName}</span>
        <ChevronDown className="h-4 w-4" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="p-2">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-2">
                {t('languageSelector.selectLanguage', 'Select Language')}
              </div>
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-50 flex items-center space-x-3 ${
                    language.code === i18n.language
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-gray-700'
                  }`}
                >
                  <span className="text-lg">{language.flag}</span>
                  <div className="flex-1">
                    <div className="font-medium">{language.nativeName}</div>
                    <div className="text-xs text-gray-500">{language.name}</div>
                  </div>
                  {language.code === i18n.language && (
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};